/**
 * Image Compression Utility for NFC-e Tracker AI Scanner
 * Resizes and converts high-resolution camera photos to optimized WebP in memory
 */

/**
 * Resizes and compresses an image file to WebP (max 720px, 72% quality)
 * @param {File|Blob} imageFile 
 * @param {number} maxDim Maximum dimension in pixels (default 720)
 * @param {number} quality WebP quality 0.0 to 1.0 (default 0.72)
 * @returns {Promise<{blob: Blob, width: number, height: number, sizeBytes: number}>}
 */
export async function compressImageForAI(imageFile, maxDim = 720, quality = 0.72) {
  let imgBitmap;
  let originalWidth = 0;
  let originalHeight = 0;

  try {
    if (typeof createImageBitmap === 'function') {
      imgBitmap = await createImageBitmap(imageFile);
      originalWidth = imgBitmap.width;
      originalHeight = imgBitmap.height;
    }
  } catch (e) {
    console.warn('[ImageCompressor] createImageBitmap fallback to Image element:', e);
  }

  // Fallback to HTMLImageElement if createImageBitmap fails
  if (!imgBitmap) {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      const url = URL.createObjectURL(imageFile);
      el.onload = () => {
        URL.revokeObjectURL(url);
        resolve(el);
      };
      el.onerror = (err) => {
        URL.revokeObjectURL(url);
        reject(err);
      };
      el.src = url;
    });
    originalWidth = img.naturalWidth || img.width;
    originalHeight = img.naturalHeight || img.height;
    imgBitmap = img;
  }

  // Calculate target dimensions keeping aspect ratio
  let targetWidth = originalWidth;
  let targetHeight = originalHeight;

  if (targetWidth > maxDim || targetHeight > maxDim) {
    if (targetWidth > targetHeight) {
      targetHeight = Math.round((targetHeight * maxDim) / targetWidth);
      targetWidth = maxDim;
    } else {
      targetWidth = Math.round((targetWidth * maxDim) / targetHeight);
      targetHeight = maxDim;
    }
  }

  targetWidth = Math.max(targetWidth, 50);
  targetHeight = Math.max(targetHeight, 50);

  // Use OffscreenCanvas if supported
  if (typeof OffscreenCanvas !== 'undefined') {
    try {
      const offscreen = new OffscreenCanvas(targetWidth, targetHeight);
      const ctx = offscreen.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(imgBitmap, 0, 0, targetWidth, targetHeight);
        const blob = await offscreen.convertToBlob({ type: 'image/webp', quality });
        return {
          blob,
          width: targetWidth,
          height: targetHeight,
          sizeBytes: blob.size
        };
      }
    } catch (err) {
      console.warn('[ImageCompressor] OffscreenCanvas error, falling back to DOM canvas:', err);
    }
  }

  // Standard Canvas Fallback
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Não foi possível inicializar o contexto Canvas 2D');
  }

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, targetWidth, targetHeight);
  ctx.drawImage(imgBitmap, 0, 0, targetWidth, targetHeight);

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('Falha ao gerar Blob WebP'));
      },
      'image/webp',
      quality
    );
  });

  return {
    blob,
    width: targetWidth,
    height: targetHeight,
    sizeBytes: blob.size
  };
}
